/**
 * Razorpay payment integration
 *
 * Setup steps:
 * 1. Add your Razorpay Key ID to .env.local:
 *      REACT_APP_RAZORPAY_KEY_ID=rzp_live_XXXXXXXXXXXXXXXX
 * 2. Your backend (Supabase Edge Function) must create the Razorpay order
 *    and return { orderId, amount, currency } — never expose your Key Secret in the browser.
 * 3. Call openRazorpay(plan, user, onSuccess) from the pricing page.
 */

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

/** Loads the Razorpay checkout script once. Returns a promise. */
function loadScript() {
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise(resolve => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * Creates a Razorpay order via a Supabase Edge Function, then opens the
 * Razorpay checkout modal.
 *
 * @param {object} plan   — a plan object from data/plans.js
 * @param {object} user   — Supabase user object { id, email }
 * @param {boolean} annual
 * @param {function} onSuccess — called with { razorpay_payment_id, plan_id }
 */
export async function openRazorpay(plan, user, annual, onSuccess) {
  if (!RAZORPAY_KEY) {
    console.warn("[Razorpay] REACT_APP_RAZORPAY_KEY_ID not set — payments disabled.");
    return;
  }
  if (plan.id === "free") { window.location.href = "/app"; return; }
  if (plan.ctaLink?.startsWith("mailto:")) { window.location.href = plan.ctaLink; return; }

  const loaded = await loadScript();
  if (!loaded) { alert("Payment gateway could not be loaded. Check your internet connection."); return; }

  const amountPaise = (annual && plan.annualPrice ? plan.annualPrice : plan.price) * 100;

  // ── Step 1: create order on your backend ──────────────────────────────────
  // Replace this URL with your Supabase Edge Function URL:
  //   supabase functions new create-razorpay-order
  const FN_BASE = (process.env.REACT_APP_SUPABASE_URL || "") + "/functions/v1";

  let orderId;
  try {
    // Get current session token to authenticate the Edge Function call
    const { supabase: sb } = await import("./supabase");
    const session = sb ? (await sb.auth.getSession()).data.session : null;

    const res = await fetch(`${FN_BASE}/create-razorpay-order`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({ plan_id: plan.id, annual, user_id: user?.id }),
    });
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();
    orderId = data.orderId;
  } catch (e) {
    console.error("[Razorpay] Order creation failed:", e);
    alert("Could not initiate payment. Please try again or contact " + process.env.REACT_APP_SUPPORT_EMAIL);
    return;
  }

  // ── Step 2: open checkout ─────────────────────────────────────────────────
  const options = {
    key:          RAZORPAY_KEY,
    amount:       amountPaise,
    currency:     "INR",
    name:         "DPL Jade",
    description:  `${plan.name} plan — ${annual ? "Annual" : "Monthly"}`,
    order_id:     orderId,
    prefill: {
      email: user?.email || "",
    },
    theme: { color: "#3b82f6" },
    modal: { backdropclose: false },
    handler: async function (response) {
      try {
        // Verify payment server-side and activate subscription
        const { supabase } = await import("./supabase");
        const session = supabase ? (await supabase.auth.getSession()).data.session : null;
        const res = await fetch(`${FN_BASE}/activate-subscription`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({
            razorpay_order_id:  response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature:  response.razorpay_signature,
            plan_id: plan.id,
            annual,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Activation failed");
        onSuccess({ razorpay_payment_id: response.razorpay_payment_id, plan_id: plan.id });
      } catch (e) {
        console.error("[Razorpay] Activation failed:", e);
        alert("Payment was received but subscription activation failed. Please contact " +
          process.env.REACT_APP_SUPPORT_EMAIL + " with payment ID: " + response.razorpay_payment_id);
      }
    },
  };

  const rzp = new window.Razorpay(options);
  rzp.on("payment.failed", e => {
    console.error("[Razorpay] Payment failed:", e.error);
    alert("Payment failed: " + e.error.description);
  });
  rzp.open();
}
